import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useLoaderData } from 'react-router-dom';
import { axiosClient } from '../../../library/Service/axiosClient';
import { State } from '../../../library/Core/SmartTypes';
import Swal from 'sweetalert2';
import { ActivityDashboardService } from '../alumni-activity-dashboard/activityDashboardService';

const MAX_ROWS = 2000;

const isValidEmail = (value: any): boolean => {
    const email = String(value || '').trim();
    if (!email || email === 'undefined' || email === 'null') return false;
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
};

const getLoggedInAdminEmail = (): string => {
    const candidates: any[] = [];

    try {
        const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
        candidates.push(
            sessionUser?.registeredEmailId,
            sessionUser?.registered_email_id,
            sessionUser?.email,
            sessionUser?.emailId,
            sessionUser?.userName,
            sessionUser?.username
        );
    } catch (_) {}

    try {
        const localUser = JSON.parse(localStorage.getItem('user') || '{}');
        candidates.push(
            localUser?.registeredEmailId,
            localUser?.registered_email_id,
            localUser?.email,
            localUser?.emailId,
            localUser?.userName,
            localUser?.username
        );
    } catch (_) {}

    try {
        const uniUser = JSON.parse(localStorage.getItem('univariety-user') || '{}');
        candidates.push(
            uniUser?.registeredEmailId,
            uniUser?.registered_email_id,
            uniUser?.email,
            uniUser?.emailId,
            uniUser?.userName,
            uniUser?.username
        );
    } catch (_) {}

    candidates.push(
        sessionStorage.getItem('user-email-id'),
        localStorage.getItem('user-email-id')
    );

    for (const value of candidates) {
        if (isValidEmail(value)) return String(value).trim();
    }
    return '';
};

type PageTab = 'upload' | 'programs';

const UserUploadLayout = (props: any) => {
    const { internal } = useLoaderData() as State;
    const [activeTab, setActiveTab] = useState<PageTab>('upload');
    const [file, setFile] = useState<File | null>(null);
    const [usertype, setUsertype] = useState('RAW_PROFILE');
    const [isLoading, setIsLoading] = useState(false);
    const [isQueueInprogress, setIsQueueInprogress] = useState(internal?.userQueueStatus === 1 ? false : true);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [responseMessage, setResponseMessage] = useState<{
        processed: number;
        invalid: number;
        totalRows?: number;
        message?: string;
        emailReport?: { success?: boolean; to?: string; message?: string };
    } | null>(null);

    interface InvalidRecord {
        record: any;
        errors: string[];
        rowNumber?: number;
    }

    const [invalidData, setInvalidData] = useState<InvalidRecord[]>([]);
    const [failedRows, setFailedRows] = useState<Array<{ rowNumber: number; errors: string[]; record?: any }>>([]);
    const navigate = useNavigate();
    const location = useLocation();

    // Download Programs by Institute (Group + Institute like Vibrancy)
    const [groups, setGroups] = useState<any[]>([]);
    const [selectedGroupId, setSelectedGroupId] = useState<string | number | null>(null);
    const [selectedGroupValue, setSelectedGroupValue] = useState('');
    const [instSearch, setInstSearch] = useState('');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [instLoading, setInstLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [instSelected, setInstSelected] = useState(false);
    const [selectedInstitute, setSelectedInstitute] = useState<{ id: number | string; name: string } | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const handleUserTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setUsertype(event.target.value);
    };

    useEffect(() => {
        if (isQueueInprogress && activeTab === 'upload') {
            Swal.fire({
                title: 'Queue is in progress...!',
                html: `
                    <div style="display: flex; flex-direction: column; align-items: center;">
                        <div class="swal2-spinner" style="margin-bottom: 16px;">
                            <svg width="40" height="40" viewBox="0 0 40 40">
                                <circle cx="20" cy="20" r="18" stroke="#3085d6" stroke-width="4" fill="none" stroke-linecap="round">
                                    <animate attributeName="stroke-dasharray" values="1,150;90,150;90,150" dur="1.5s" repeatCount="indefinite"/>
                                    <animate attributeName="stroke-dashoffset" values="0;-35;-124" dur="1.5s" repeatCount="indefinite"/>
                                    <animate attributeName="stroke" values="#3085d6;#3fc3ee;#87adbd;#3085d6" dur="6s" repeatCount="indefinite"/>
                                </circle>
                            </svg>
                        </div>
                        <div>Please come back after a while..!!</div>
                    </div>
                `,
                icon: 'warning',
                allowOutsideClick: false,
                showCloseButton: false,
                allowEscapeKey: false,
                showConfirmButton: true,
                confirmButtonText: 'Refresh',
                showDenyButton: true,
                denyButtonText: 'Go Back To Previous Page',
                preConfirm: () => {
                    navigate(0);
                },
                preDeny: () => {
                    navigate(-1);
                },
            });
        } else {
            Swal.close();
        }
        return () => {
            Swal.close();
        };
    }, [isQueueInprogress, isFormSubmitted, navigate, location, activeTab]);

    useEffect(() => {
        if (activeTab !== 'programs') return;
        ActivityDashboardService.getGroups()
            .then((r) => setGroups(r.data?.data || []))
            .catch(() => setGroups([]));
    }, [activeTab]);

    useEffect(() => {
        if (instSelected) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (instSearch.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            setInstLoading(true);
            try {
                const r = await ActivityDashboardService.searchInstitutes(
                    instSearch,
                    selectedGroupId ?? undefined
                );
                const list = r.data?.data || [];
                setSuggestions(list);
                setShowSuggestions(true);
            } catch {
                setSuggestions([]);
                setShowSuggestions(true);
            } finally {
                setInstLoading(false);
            }
        }, 300);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [instSearch, instSelected, selectedGroupId]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleGroupChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setSelectedGroupValue(val);
        const opt = groups.find((g: any) => String(g.id) === val);
        setSelectedGroupId(opt ? opt.id : null);
        setInstSearch('');
        setInstSelected(false);
        setSuggestions([]);
        setShowSuggestions(false);
        setSelectedInstitute(null);
    };

    const handleSelectInst = (inst: any) => {
        setInstSearch(inst.name || '');
        setInstSelected(true);
        setShowSuggestions(false);
        setSuggestions([]);
        setSelectedInstitute({ id: inst.id, name: inst.name || '' });
    };

    const handleClearInst = () => {
        setInstSearch('');
        setInstSelected(false);
        setSuggestions([]);
        setSelectedInstitute(null);
    };

    const handleDownloadPrograms = async () => {
        if (!selectedInstitute?.id) {
            alert('Please select an institute from the dropdown.');
            return;
        }

        const url = `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/raw-user/programs-by-institute`;
        try {
            setIsDownloading(true);
            const response = await axiosClient().get(url, {
                params: { institute_id: selectedInstitute.id },
                responseType: 'blob',
            });

            const contentType = String(response.headers?.['content-type'] || '');
            if (contentType.includes('application/json')) {
                const text = await (response.data as Blob).text();
                let message = 'Failed to download programs';
                try {
                    message = JSON.parse(text)?.message || message;
                } catch (_) {}
                alert(message);
                return;
            }

            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const objectUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = `programs_institute_${selectedInstitute.id}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (error: any) {
            console.error('Error downloading programs:', error);
            let message = 'Failed to download programs for institute';
            try {
                const data = error?.response?.data;
                if (data instanceof Blob) {
                    const text = await data.text();
                    message = JSON.parse(text)?.message || message;
                } else if (data?.message) {
                    message = data.message;
                }
            } catch (_) {}
            alert(message);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFile(e.target.files[0]);
            setResponseMessage(null);
            setInvalidData([]);
            setFailedRows([]);
            setIsFormSubmitted(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!file) {
            console.error('No file selected');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('usertype', usertype);

        // Resolve logged-in SUPER_ADMIN email for report delivery
        const reportToEmail = getLoggedInAdminEmail();
        if (!reportToEmail) {
            alert('Could not find your login email. Please re-login as SUPER_ADMIN and try again.');
            return;
        }
        formData.append('reportToEmail', reportToEmail);

        const url = `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/raw-user/upload-spread-sheet`;

        try {
            setIsLoading(true);
            setUploadProgress(0);

            const uploadResponse = await axiosClient().post(url, formData, {
                onUploadProgress: (progressEvent) => {
                    if (progressEvent && progressEvent.loaded !== undefined && progressEvent.total !== undefined) {
                        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                        setUploadProgress(progress);
                    } else {
                        console.warn('Progress information is missing or incomplete.');
                        setUploadProgress(0);
                    }
                },
            });

            if (uploadResponse.data.usersInQueue > 0) {
                setIsQueueInprogress(true);
            }

            const {
                message,
                invalidRecords,
                data,
                invalidData: invalidPayload,
                failedRows: failedPayload,
                totalRows,
                successCount,
                emailReport,
            } = uploadResponse.data;

            setInvalidData(invalidPayload || []);
            setFailedRows(failedPayload || []);
            setResponseMessage({
                processed: successCount ?? data?.length ?? 0,
                invalid: invalidRecords ?? failedPayload?.length ?? 0,
                totalRows: totalRows,
                message,
                emailReport,
            });
            setIsFormSubmitted(true);
        } catch (error) {
            console.error('Error uploading file:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const rowsToShow =
        failedRows.length > 0
            ? failedRows
            : invalidData.map((item, index) => ({
                  rowNumber: item.rowNumber ?? index + 1,
                  errors: item.errors,
                  record: item.record,
              }));

    return (
        <div className="container max-1140 px-lg-0">
            <div className="row">
                <div className="col-md-12 mb-4">
                    <div className="container mt-5">
                        <h2 className="mb-4">Upload Excel Data</h2>

                        <ul className="nav nav-tabs mb-3">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link ${activeTab === 'upload' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('upload')}>
                                    Upload Excel
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link ${activeTab === 'programs' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('programs')}>
                                    Download Programs
                                </button>
                            </li>
                        </ul>

                        {activeTab === 'upload' && (
                            <>
                        <div className="alert alert-info">
                            <h5>Please Note:</h5>
                            <ul>
                                <li>The file must be in Excel format (.xlsx). Max {MAX_ROWS} rows.</li>
                                <li>
                                    <strong>Raw User / Semi-Verified</strong>: use only{' '}
                                    <strong>Download Sample Excel File</strong> (Bulk Alumni). Wrong file format will be rejected.
                                </li>
                                <li>
                                    <strong>Bulk Student Signup</strong>: use only{' '}
                                    <strong>Download Bulk Student Signup Sample File</strong>. Wrong file format will be rejected.
                                </li>
                                <li>
                                    Alumni file mandatory columns: <code>name</code>, <code>Email_id</code>,{' '}
                                    <code>institute_id</code>, <code>program</code> or <code>program_id</code>.{' '}
                                    <code>course_end_year</code> / passout year is optional (if filled, must be a valid
                                    year). Missing mandatory headers or blank values will fail. Email and
                                    Phone must be valid (spaces are removed automatically). Name allows letters and
                                    spaces only (no <code>,</code> <code>/</code> <code>-</code> etc.). Same{' '}
                                    <code>Email_id</code> cannot already exist for that institute. If{' '}
                                    <code>gender</code> is filled, it must be exactly <code>MALE</code>,{' '}
                                    <code>FEMALE</code> or <code>OTHER</code> (uppercase only).
                                </li>
                                <li>
                                    Student file mandatory columns: <code>name</code>, <code>program_id</code>,{' '}
                                    <code>Email_id</code>, <code>mobile</code>, <code>institute_id</code>,{' '}
                                    <code>start_year</code>, <code>passout_year</code>.{' '}
                                    <code>12th_passout_year</code> is optional (if filled, must be a valid year). Name
                                    allows letters and spaces only; email/mobile spaces are stripped. Failed rows show
                                    the reason and are not inserted.{' '}
                                    <code>program_id</code> must belong to that <code>institute_id</code>; same{' '}
                                    <code>Email_id</code> cannot repeat for the same institute in the sheet or already
                                    exist. If <code>gender</code> is filled, it must be exactly <code>MALE</code>,{' '}
                                    <code>FEMALE</code> or <code>OTHER</code>.
                                </li>
                                <li>
                                    Optional <code>profile_image_url</code>: must be a direct public image link
                                    (http/https, not a Drive/LinkedIn page). It is downloaded and saved to our storage;
                                    if the image cannot be downloaded, that row fails with the reason and is not
                                    inserted. Rows with images take longer to process.
                                </li>
                            </ul>
                            <a
                                href="https://company-logo-list.s3.ap-south-1.amazonaws.com/sample_dev_file.xlsx"
                                className="btn btn-outline-primary me-2"
                                download>
                                Download Sample Excel File
                            </a>
                            <a
                                href="https://company-logo-list.s3.ap-south-1.amazonaws.com/Bulk+Student+Signup+Sample+File.xlsx"
                                className="btn btn-outline-primary"
                                download>
                                Download Bulk Student Signup Sample File
                            </a>
                        </div>

                        <form onSubmit={handleSubmit} encType="multipart/form-data">
                            <div className="mb-3">
                                <label htmlFor="user_type" className="form-label">
                                    Select User Type
                                </label>
                                <select className="form-control" name="user_type" id="user_type" onChange={handleUserTypeChange}>
                                    <option value="RAW_PROFILE">Raw User</option>
                                    <option value="SEMI_VERIFIED_USER">Semi-Verified User</option>
                                    <option value="PROFILE_IN_PROGRESS">Bulk Student Signup</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label htmlFor="fileInput" className="form-label">
                                    Choose file (Only Excel, max {MAX_ROWS} rows)
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="fileInput"
                                    accept=".xlsx"
                                    onChange={handleFileChange}
                                    required
                                />
                            </div>
                            {!isFormSubmitted && (
                                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                    {isLoading ? 'Uploading...' : 'Upload'}
                                </button>
                            )}
                        </form>

                        {isLoading && (
                            <div className="progress mt-3">
                                <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{ width: `${uploadProgress}%` }}
                                    aria-valuenow={uploadProgress}
                                    aria-valuemin={0}
                                    aria-valuemax={100}>
                                    {uploadProgress}%
                                </div>
                            </div>
                        )}
                        {isLoading && uploadProgress === 100 && (
                            <div className="text-muted font-13 mt-2">
                                File received. Validating rows and saving profile images, please wait...
                            </div>
                        )}

                        {!isQueueInprogress && responseMessage && (
                            <div className="mt-4">
                                <h5>Upload Summary</h5>
                                {responseMessage.message && (
                                    <div
                                        className={`alert ${
                                            responseMessage.invalid === 0 ? 'alert-success' : 'alert-warning'
                                        } p-2`}>
                                        {responseMessage.message}
                                    </div>
                                )}
                                <p>
                                    <strong>Total Rows:</strong>{' '}
                                    {responseMessage.totalRows ?? responseMessage.processed + responseMessage.invalid}
                                </p>
                                <p>
                                    <strong>Successfully uploaded:</strong> {responseMessage.processed}
                                </p>
                                <p>
                                    <strong>Failed:</strong> {responseMessage.invalid}
                                </p>
                                {responseMessage.emailReport && (
                                    <div
                                        className={`alert ${
                                            responseMessage.emailReport.success ? 'alert-success' : 'alert-danger'
                                        } p-2`}>
                                        {responseMessage.emailReport.success
                                            ? `Report email sent to ${responseMessage.emailReport.to || 'your login email'}`
                                            : `Report email NOT sent: ${
                                                  responseMessage.emailReport.message || 'broadcast/SendGrid failed'
                                              }`}
                                    </div>
                                )}
                            </div>
                        )}

                        {rowsToShow.length > 0 && (
                            <div className="mt-3">
                                <h5 className="text-danger">Failed Rows ({rowsToShow.length})</h5>
                                <div className="border rounded p-2" style={{ maxHeight: 320, overflowY: 'auto' }}>
                                    {rowsToShow.slice(0, 100).map((item, index) => (
                                        <div key={index} className="mb-2 font-13">
                                            <b>Row {item.rowNumber}:</b>{' '}
                                            {Array.isArray(item.errors) ? item.errors.join(' | ') : String(item.errors || '')}
                                            {item.record?.Email_id ? (
                                                <span className="text-muted"> ({item.record.Email_id})</span>
                                            ) : null}
                                            {index < Math.min(rowsToShow.length, 100) - 1 ? <hr className="my-1" /> : null}
                                        </div>
                                    ))}
                                    {rowsToShow.length > 100 && (
                                        <div className="text-muted font-13">
                                            ...and {rowsToShow.length - 100} more failed rows.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                            </>
                        )}

                        {activeTab === 'programs' && (
                            <div className="bg-white border rounded shadow-sm p-3" style={{ overflow: 'visible' }}>
                                <div className="alert alert-info mb-3">
                                    <h5 className="mb-2">Download Programs by Institute</h5>
                                    <p className="mb-0">
                                        Optionally filter by group, then search and select an institute (name and
                                        institute_id are shown). Download CSV columns: <code>id</code>,{' '}
                                        <code>name</code>, <code>program_level</code>.
                                    </p>
                                </div>

                                <div className="row g-3 align-items-end" style={{ overflow: 'visible' }}>
                                    <div className="col-md-3">
                                        <label className="form-label small fw-semibold text-uppercase text-muted mb-1">
                                            <i className="bi bi-buildings me-1"></i>Group
                                        </label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={selectedGroupValue}
                                            onChange={handleGroupChange}>
                                            <option value="">All Groups</option>
                                            {groups.map((g: any) => (
                                                <option key={g.id} value={g.id}>
                                                    {g.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div
                                        className="col-md-6"
                                        ref={wrapperRef}
                                        style={{ position: 'relative', overflow: 'visible', zIndex: 20 }}>
                                        <label className="form-label small fw-semibold text-uppercase text-muted mb-1">
                                            <i className="bi bi-mortarboard me-1"></i>Institute
                                        </label>
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text bg-white">
                                                {instLoading ? (
                                                    <span
                                                        className="spinner-border spinner-border-sm"
                                                        style={{ width: 12, height: 12 }}
                                                    />
                                                ) : (
                                                    <i className="bi bi-search text-muted"></i>
                                                )}
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search institute… (min 2 chars)"
                                                value={instSearch}
                                                autoComplete="off"
                                                onChange={(e) => {
                                                    setInstSearch(e.target.value);
                                                    setInstSelected(false);
                                                    setSelectedInstitute(null);
                                                }}
                                                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                                            />
                                            {instSearch && (
                                                <button
                                                    className="btn btn-outline-secondary btn-sm"
                                                    type="button"
                                                    onClick={handleClearInst}>
                                                    ✕
                                                </button>
                                            )}
                                        </div>

                                        {showSuggestions && (
                                            <div
                                                className="border rounded bg-white shadow position-absolute start-0 end-0 mt-1"
                                                style={{
                                                    zIndex: 1050,
                                                    maxHeight: 300,
                                                    overflowY: 'auto',
                                                    top: '100%',
                                                    left: 0,
                                                    right: 0,
                                                }}>
                                                {suggestions.length > 0 ? (
                                                    suggestions.map((inst: any) => (
                                                        <div
                                                            key={inst.id}
                                                            className="px-3 py-2 border-bottom"
                                                            style={{ cursor: 'pointer' }}
                                                            onMouseDown={(e) => {
                                                                e.preventDefault();
                                                                handleSelectInst(inst);
                                                            }}>
                                                            <div className="fw-semibold font-13">{inst.name}</div>
                                                            <div className="text-muted" style={{ fontSize: 12 }}>
                                                                institute_id: {inst.id}
                                                            </div>
                                                            {inst.group_name && (
                                                                <div className="text-muted" style={{ fontSize: 11 }}>
                                                                    {inst.group_name}
                                                                </div>
                                                            )}
                                                            {inst.city_name && (
                                                                <div className="text-muted" style={{ fontSize: 11 }}>
                                                                    <i className="bi bi-geo-alt me-1"></i>
                                                                    {inst.city_name}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="px-3 py-2 text-muted small">
                                                        No institutes found.
                                                        {selectedGroupId
                                                            ? ' Try a different name or clear the group filter.'
                                                            : ' Try a different name.'}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-md-3">
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm w-100"
                                            disabled={!selectedInstitute?.id || isDownloading}
                                            onClick={handleDownloadPrograms}>
                                            {isDownloading ? 'Downloading...' : 'Download'}
                                        </button>
                                    </div>
                                </div>

                                {selectedInstitute && (
                                    <div className="mt-3 text-muted small">
                                        Selected: <strong>{selectedInstitute.name}</strong> (institute_id:{' '}
                                        {selectedInstitute.id})
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserUploadLayout;
